import "../spinner";
import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { shared } from "../../styles";
import "../scroller";
import "../date-input";
import "../popover";
import "../issue";
import "../sortable-list";

export type TileConfig = {
    label: string;
    icon: string;
    size:
        | number
        | {
              inline: number;
              block: number;
          };
};

@customElement("ptc-dashboard-tile")
export class DashboardTile extends LitElement {
    @property({ type: Boolean })
    loading = false;

    @property({ type: String })
    label: TileConfig["label"];

    @property({ attribute: false })
    size: TileConfig["size"];

    @property({ type: String })
    icon: TileConfig["icon"];

    static styles = [
        shared,
        css`
            :host {
                --tile-size: 1;

                min-height: var(--tile-base-size);
                height: calc(
                    var(--tile-size-block, var(--tile-size)) * (var(--tile-base-size) + var(--tile-gap)) - var(
                            --tile-gap
                        )
                );
                display: grid;
                grid-column: span var(--tile-size-inline, var(--tile-size));
                grid-row: span var(--tile-size-block, var(--tile-size));
                overflow: hidden;

                outline: 1px solid var(--shade-5);
                border-radius: var(--border-radius);
                display: grid;
                grid-template-rows: min-content auto;
                background: var(--color-bg-light);
            }

            :host(:empty) {
                opacity: 0.5;

                [part="header"] {
                    border-block-end: 1px solid transparent;
                }
            }

            :host(:empty) {
                opacity: 0.5;

                [part="header"] {
                    border-block-end: 1px solid transparent;
                }
            }

            ::slotted(*) {
                box-sizing: border-box;
                padding: var(--tile-content-padding, 1rem);
            }

            [part="content"] {
                overflow: auto;
                display: grid;
            }

            [part="header"] {
                display: flex;
                align-items: center;
                justify-content: space-between;
                font-size: 1rem;
                font-weight: 600;
                border-block-end: 1px solid var(--shade-5);
                padding: 0.5rem 1rem;
                user-select: none;
                height: min-content;
            }

            [part="header"][hidden] ~ [part="content"] {
                grid-row: span 2;
            }

            [part="spinner"],
            [part="icon"] {
                width: 1lh;
                height: 1lh;

                transition: opacity 0.2s ease-in-out;

                @starting-style {
                    opacity: 0;
                }
            }

            [part="icon"] {
                display: grid;
                place-content: center;
                line-height: inherit;
                color: var(--tile-icon-color);
            }
        `,
    ];

    connectedCallback() {
        super.connectedCallback();
    }

    render() {
        if (typeof this.size === "number") {
            this.style.setProperty("--tile-size", this.size.toString());
        } else {
            this.style.setProperty("--tile-size-inline", this.size.inline.toString());
            this.style.setProperty("--tile-size-block", this.size.block.toString());
        }

        return html`
            <header part="header" ?hidden=${!this.label && !this.icon}>
                ${this.label}
                <slot name="header"></slot>
                ${this.icon ? html`<i part="icon" class="fa-solid fa-${this.icon}"></i>` : nothing}
            </header>
            <div part="content"><slot></slot></div>
        `;
    }
}
